import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks';
import { Button, Input, Card } from '@/components';
import { validateEmail, validatePassword } from '@/utils/validation';
import { getIntendedDestination, useTokenExpiryNotification } from '@/utils/redirects';
import type { LoginRequest } from '@shared/types';

interface LoginFormData {
  email: string;
  password: string;
}

interface FormErrors {
  email: string;
  password: string;
  submit: string;
}

/**
 * Sign in page with form validation
 */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState<FormErrors>({
    email: '',
    password: '',
    submit: '',
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({
    email: false,
    password: false,
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const { login, loginWithGoogle, isAuthenticated } = useAuth();

  // Listen for token expiry notifications
  useTokenExpiryNotification(() => {
    setSuccessMessage(null);
    setErrors((prev) => ({
      ...prev,
      submit: 'Your session has expired. Please log in again.',
    }));
  });

  useEffect(() => {
    document.title = 'Print WhatsApp Orders in 1-Click Without Downloading | PrintDeck Direct';

    // Clear recovery redirect flag when visiting login page
    sessionStorage.removeItem('supabase_recovery_redirect');

    // Show success message if redirected from signup/password reset
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setSuccessMessage(state.message);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location.state]);

  // Redirect after successful login (isAuthenticated will flip to true via onAuthStateChange)
  useEffect(() => {
    if (isAuthenticated) {
      const destination = getIntendedDestination('/dashboard');
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors.submit) {
      setErrors((prev) => ({ ...prev, submit: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name);
  };

  const validateField = (fieldName: string) => {
    let error = '';

    switch (fieldName) {
      case 'email':
        error = validateEmail(formData.email) || '';
        break;
      case 'password':
        error = validatePassword(formData.password) || '';
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return error === '';
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      email: validateEmail(formData.email) || '',
      password: validatePassword(formData.password) || '',
      submit: '',
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === '');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccessMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsEmailSubmitting(true);

    try {
      const credentials: LoginRequest = {
        email: formData.email,
        password: formData.password,
      };
      await login(credentials);
      // Navigation is handled by the useEffect above that watches isAuthenticated
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setErrors((prev) => ({ ...prev, submit: message }));
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  const isFormValid = !Object.values(errors).some((error) => error !== '');

  return (
    <div className="min-h-screen px-4 py-4 lg:px-8 bg-white flex flex-col justify-between overflow-hidden">
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-6xl grid items-center gap-6 lg:grid-cols-[1fr_1fr]">
        {/* LEFT SECTION - Design match */}
        <section className="relative flex flex-col justify-center space-y-6 pr-8 lg:pr-16">

          {/* Top Logo */}
          <div className="flex items-center gap-2 mb-2">
            <img src="/logo-icon.png" alt="PrintDeck Logo" className="w-8 h-8 object-contain" width={32} height={32} />
            <span className="text-xl font-bold text-slate-900 tracking-tight">PrintDeck Direct</span>
          </div>

          {/* Headline Area */}
          <div className="relative z-10">
            {/* Paper Airplane Decorative */}
            <div className="absolute -top-12 -right-4 w-32 h-32 opacity-80 pointer-events-none hidden md:block animate-float">
              <style dangerouslySetInnerHTML={{ __html: `
                @keyframes flight-loop {
                  0% {
                    transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
                    opacity: 0.85;
                  }
                  15% {
                    transform: translate3d(3px, -6px, 0) rotate(1deg) scale(1.02);
                    opacity: 0.85;
                  }
                  30% {
                    transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
                    opacity: 0.85;
                  }
                  /* Take off: climb fast to the top right and fade out */
                  45% {
                    transform: translate3d(240px, -240px, 0) rotate(15deg) scale(0.3);
                    opacity: 0;
                  }
                  /* Position to bottom-left background while invisible */
                  46% {
                    transform: translate3d(-180px, 180px, 0) rotate(-25deg) scale(0.3);
                    opacity: 0;
                  }
                  /* Swoop back in from bottom-left */
                  65% {
                    transform: translate3d(15px, -15px, 0) rotate(5deg) scale(1.05);
                    opacity: 0.85;
                  }
                  72% {
                    transform: translate3d(-5px, 5px, 0) rotate(-2deg) scale(0.98);
                    opacity: 0.85;
                  }
                  80% {
                    transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
                    opacity: 0.85;
                  }
                  100% {
                    transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
                    opacity: 0.85;
                  }
                }
                .animate-float {
                  animation: flight-loop 10s ease-in-out infinite;
                }
              `}} />
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 50 L90 20 L50 90 L40 60 Z" fill="#60A5FA" opacity="0.8" />
                <path d="M40 60 L90 20 L50 70 Z" fill="#3B82F6" />
                <path d="M40 60 L50 70 L45 80 Z" fill="#2563EB" />
                <path d="M90 20 Q 80 50 60 90" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="4 4" fill="none" />
              </svg>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 mb-4 border border-emerald-100">
              <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              <span className="text-[11px] font-bold text-emerald-800 tracking-wider">BUILT FOR PRINT SHOPS</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1] tracking-tight">
              WhatsApp In.<br />
              One-Click <span className="text-blue-600">Print Out.</span>
            </h1>
            <p className="mt-3 text-base text-slate-500 max-w-md leading-relaxed">
              PrintDeck Direct turns WhatsApp orders into print-ready jobs. No downloads. No extra steps.
            </p>
          </div>

          {/* Workflow Cards */}
          <div className="flex items-center gap-3 w-full max-w-2xl mt-4">
            {/* Card 1 */}
            <div className="relative flex-1 bg-white rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col items-center text-center group hover:-translate-y-1 transition-transform">
              <div className="absolute -top-3 -left-3 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">1</div>
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.66-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
              </div>
              <h3 className="font-bold text-slate-900 text-[13px]">Customer Sends</h3>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-tight">Files & instructions<br />on WhatsApp</p>
            </div>

            {/* Arrow */}
            <div className="text-slate-300 hidden sm:block">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" strokeDasharray="3 3" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            {/* Card 2 */}
            <div className="relative flex-1 bg-white rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col items-center text-center group hover:-translate-y-1 transition-transform">
              <div className="absolute -top-3 -left-3 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">2</div>
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm0 2v12h16V6H4zm4 4h8v2H8v-2z" /></svg>
              </div>
              <h3 className="font-bold text-slate-900 text-[13px]">You Receive</h3>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-tight">Orders auto-arrive in<br />your PrintDeck</p>
            </div>

            {/* Arrow */}
            <div className="text-slate-300 hidden sm:block">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" strokeDasharray="3 3" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            {/* Card 3 */}
            <div className="relative flex-1 bg-white rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col items-center text-center group hover:-translate-y-1 transition-transform">
              <div className="absolute -top-3 -left-3 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">3</div>
              <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24"><path d="M18 7H6V3h12v4zm0 2h2c1.1 0 2 .9 2 2v6h-4v4H6v-4H2v-6c0-1.1.9-2 2-2h14zm-2 10v-4H8v4h8zm1.5-6.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" /></svg>
              </div>
              <h3 className="font-bold text-slate-900 text-[13px]">One-Click Print</h3>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-tight">Preview & print<br />instantly</p>
            </div>
          </div>

          {/* Dark Banner */}
          <div className="bg-[#2c3258] rounded-2xl p-4 mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <div>
                <h4 className="text-white font-semibold text-xs">Save Time</h4>
                <p className="text-slate-300 text-[10px] mt-0.5">Automate busy work</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>
              </div>
              <div>
                <h4 className="text-white font-semibold text-xs">Fewer Errors</h4>
                <p className="text-slate-300 text-[10px] mt-0.5">No manual downloads</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M3 3h2v18H3V3zm6 8h2v10H9V11zm6-4h2v14h-2V7zm6 6h2v8h-2v-8z" /></svg>
              </div>
              <div>
                <h4 className="text-white font-semibold text-xs">More Profit</h4>
                <p className="text-slate-300 text-[10px] mt-0.5">Serve more customers</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" /></svg>
              </div>
              <div>
                <h4 className="text-white font-semibold text-xs">Highly Secure</h4>
                <p className="text-slate-300 text-[10px] mt-0.5">Your data, protected</p>
              </div>
            </div>
          </div>

          {/* Bottom links */}
          <div className="flex items-center gap-6 text-[11px] font-semibold text-slate-500 mt-4 pl-2">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Secure & Reliable
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Real-time Updates
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>
              Built to Grow
            </div>
          </div>

        </section>

        {/* RIGHT SECTION - Login Form */}
        <Card className="relative mx-auto w-full max-w-md !rounded-[24px] overflow-hidden border border-slate-200/80 bg-white/95 p-0 shadow-[0_24px_70px_rgba(15,23,42,0.16)]">
          <div className="border-b border-slate-200/80 bg-gradient-to-r from-blue-50 to-emerald-50 px-8 py-5 backdrop-blur-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Sign in to PrintDeck</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">Welcome</h2>
            <p className="mt-1 text-xs text-slate-600">Enter your credentials to continue</p>
          </div>

          <div className="space-y-5 px-8 py-6">

            {/* Success Message */}
            {successMessage && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"
                role="status"
              >
                <p className="text-xs text-emerald-900">✓ {successMessage}</p>
              </div>
            )}

            {/* Error Banner */}
            {errors.submit && (
              <div
                className="rounded-lg border border-red-200 bg-red-50 p-3"
                role="alert"
              >
                <p className="text-xs text-red-800">✕ {errors.submit}</p>
              </div>
            )}

            {/* Google Auth */}
            <div className="mb-4 mt-2">
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  setIsGoogleSubmitting(true);
                  try {
                    await loginWithGoogle();
                  } catch (err: any) {
                    setErrors(prev => ({ ...prev, submit: err.message || 'Failed to sign in with Google' }));
                    setIsGoogleSubmitting(false);
                  }
                }}
                disabled={isEmailSubmitting || isGoogleSubmitting}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-[#747775] bg-[#f8f9fa] px-4 py-2 text-sm font-semibold text-[#1f1f1f] shadow-sm transition-all hover:bg-[#f1f3f4] focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:opacity-50"
              >
                {!isGoogleSubmitting && (
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    <path d="M1 1h22v22H1z" fill="none"/>
                  </svg>
                )}
                {isGoogleSubmitting && (
                  <svg className="animate-spin h-5 w-5 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isGoogleSubmitting ? 'Signing in...' : 'Sign in with Google'}
              </button>
            </div>

            <div className="relative mb-5 mt-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-slate-400">Or continue with email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Email */}
              <Input
                id="email"
                label="Email Address"
                type="email"
                name="email"
                placeholder="you@printshop.com"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email ? errors.email : ''}
                fullWidth
                disabled={isEmailSubmitting || isGoogleSubmitting}
                aria-invalid={touched.email && !!errors.email ? 'true' : 'false'}
                aria-describedby={
                  touched.email && errors.email ? 'email-error' : undefined
                }
              />

              {/* Password */}
              <Input
                id="password"
                label="Password"
                type="password"
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password ? errors.password : ''}
                fullWidth
                disabled={isEmailSubmitting || isGoogleSubmitting}
                aria-invalid={
                  touched.password && !!errors.password ? 'true' : 'false'
                }
                aria-describedby={
                  touched.password && errors.password ? 'password-error' : undefined
                }
              />

              {/* Forgot Password Link */}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                isLoading={isEmailSubmitting}
                disabled={isEmailSubmitting || isGoogleSubmitting}
                aria-busy={isEmailSubmitting}
              >
                {isEmailSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Footer */}
            <div className="text-center pt-2">
              <p className="text-xs text-slate-600">
                Don't have an account?{' '}
                <Link to="/signup" className="font-semibold text-blue-600 hover:text-blue-700">
                  Create one
                </Link>
              </p>
              
              <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
                By signing in, you agree to our <br/>
                <Link to="/terms" className="font-medium text-slate-500 hover:text-blue-600 hover:underline transition-colors">Terms of Service</Link> and <Link to="/privacy" className="font-medium text-slate-500 hover:text-blue-600 hover:underline transition-colors">Privacy Policy</Link>.
              </p>
            </div>

          </div>
        </Card>
      </div>
      </div>
      
      {/* Page Footer */}
      <footer className="w-full text-center py-4 border-t border-slate-100/50 mt-4 z-10">
        <p className="text-[10px] text-slate-400">
          © 2026 PrintDeck Direct (also referred to as Print Deck Direct or Direct Print Deck). All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
